import { MessagePathRecord } from "../MessagePathRecord";
import { type TopicRecord } from "../TopicRecord";

import { TopicNode, MessagePathNode } from "./node";

/**
 * Given a list of TopicRecords, construct a list of TopicNode trees.
 * MessagePaths have a `path_in_schema` field that is an array of keys,
 * which indicate their position in the tree. For example:
 * Given:
 * [
 *  {
 *    topic_name: "topic_name",
 *    message_paths: [
 *     { path_in_schema: ["foo"], ... },
 *     { path_in_schema: ["foo", "bar"], ... },
 *     { path_in_schema: ["foo", "bar", "baz"], ... }
 *    ],
 *    ...
 *  }
 * ]
 *
 * Expect:
 * {
 *   label: "topic_name",
 *   data: TopicRecord,
 *   depth: 0,
 *   children: [
 *     {
 *       label: "foo",
 *       data: MessagePathRecord,
 *       depth: 1,
 *       parent: <reference to the "topic_name" node>,
 *       children: [
 *           {
 *             label: "bar",
 *             data: MessagePathRecord,
 *             depth: 2,
 *             parent: <reference to the "foo" node>,
 *             children: [
 *               {
 *                 label: "baz",
 *                 data: MessagePathRecord,
 *                 depth: 3,
 *                 children: [],
 *                 parent: <reference to the "bar" node>
 *               }
 *           ]
 *         }
 *       ]
 *     }
 *   ]
 * }
 */
export function constructTree(topicRecords: TopicRecord[]): TopicNode[] {
  const tree: TopicNode[] = [];
  for (const topicRecord of topicRecords) {
    const topicNode = new TopicNode({
      label: topicRecord.topic_name,
      data: topicRecord,
    });
    tree.push(topicNode);

    // Sort by path_in_schema length (shorter first) so parents come before children.
    // Secondary sort by last element for stable ordering.
    const messagePathRecords = topicRecord.message_paths
      .slice()
      .sort((a, b) => {
        const lenDiff = a.path_in_schema.length - b.path_in_schema.length;
        if (lenDiff !== 0) {
          return lenDiff;
        }
        const aLabel = a.path_in_schema.at(-1) ?? "";
        const bLabel = b.path_in_schema.at(-1) ?? "";
        return aLabel.localeCompare(bLabel, undefined, { numeric: true });
      });

    for (const messagePath of messagePathRecords) {
      const pathInSchema = messagePath.path_in_schema;
      if (pathInSchema.length === 0) {
        continue;
      }
      const parents = pathInSchema.slice(0, pathInSchema.length - 1);
      const current = pathInSchema[pathInSchema.length - 1];

      let parent: TopicNode | MessagePathNode = topicNode;
      for (const parentLabel of parents) {
        const existing: MessagePathNode | undefined =
          parent.findChildByLabel(parentLabel);
        if (existing !== undefined) {
          parent = existing;
        }
      }
      parent.addChild(messagePath, current);
    }
  }
  return tree.sort((a, b) => {
    return a.label.localeCompare(b.label, undefined, { numeric: true });
  });
}

/**
 * Given a topic and a message path record, find the corresponding MessagePathNode in the tree.
 * This is useful for rendering nested message paths.
 *
 * This creates a minimal tree structure with just the nodes needed for the specific path_in_schema.
 */
export function getMessagePathNodeFromRecord(
  topic: TopicRecord,
  messagePath: MessagePathRecord,
): [MessagePathNode, TopicNode] {
  if (messagePath.path_in_schema.length === 0) {
    throw new Error("Cannot create a message path node from an empty path");
  }

  const topicNode = new TopicNode({
    data: topic,
    label: topic.topic_name,
  });

  const pathParts = messagePath.path_in_schema;

  let parent: TopicNode | MessagePathNode = topicNode;

  for (let i = 0; i < pathParts.length; i++) {
    const part = pathParts[i];

    // For the last part, use the actual message path record
    if (i === pathParts.length - 1) {
      parent = parent.addChild(messagePath, part);
      continue;
    }

    // For intermediate parts, find the actual message path record in the topic
    // by matching the path_in_schema prefix
    const expectedPrefix = pathParts.slice(0, i + 1);
    const intermediateMsgPath = topic.message_paths.find(
      (mp) =>
        mp.path_in_schema.length === expectedPrefix.length &&
        mp.path_in_schema.every((seg, idx) => seg === expectedPrefix[idx]),
    );

    if (!intermediateMsgPath) {
      throw new Error(
        `Unable to find intermediate message path ${expectedPrefix.join(".")} in topic ${topic.topic_name}`,
      );
    }

    parent = parent.addChild(intermediateMsgPath, part);
  }

  if (!(parent instanceof MessagePathNode)) {
    throw new Error("Unable to create message path node from record");
  }

  return [parent, topicNode];
}
